/**
 * One dropdown under Devices: AD-like structure (departments, custom collections).
 * Selecting filters the devices shown in OS cards and device list.
 */
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { ChevronDown, Folder, Briefcase, Plus } from 'lucide-react';
import { apiService } from '../services/api';
import { loadDirectory, DIRECTORY_UPDATED_EVENT } from '../services/directoryStore';

interface TreeNode {
  id: string;
  name: string;
  type: string;
  children?: TreeNode[];
  folderId?: string | null;
}

interface DepartmentCollectionDropdownProps {
  devices: { platform?: string; dept?: string; department?: string }[];
  value: string | null;
  onChange: (nodeId: string | null, nodeName: string) => void;
}

const deviceDept = (d: { dept?: string; department?: string }) => (d as any).dept || (d as any).department || 'Unassigned';

export const DepartmentCollectionDropdown: React.FC<DepartmentCollectionDropdownProps> = ({ devices, value, onChange }) => {
  const [open, setOpen] = useState(false);
  const [customFolders, setCustomFolders] = useState<any[]>([]);
  const [customCollections, setCustomCollections] = useState<any[]>([]);
  const [apiGroups, setApiGroups] = useState<any[]>([]);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    apiService.getDeviceGroups().then((g) => setApiGroups(Array.isArray(g) ? g : [])).catch(() => setApiGroups([]));
    loadDirectory().then((dir) => {
      setCustomFolders(dir.folders || []);
      setCustomCollections(dir.collections || []);
    }).catch(() => {});
    const onUpdate = () => {
      loadDirectory().then((dir) => {
        setCustomFolders(dir.folders || []);
        setCustomCollections(dir.collections || []);
      }).catch(() => {});
    };
    window.addEventListener(DIRECTORY_UPDATED_EVENT, onUpdate);
    return () => window.removeEventListener(DIRECTORY_UPDATED_EVENT, onUpdate);
  }, []);

  useEffect(() => {
    const onOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    if (open) document.addEventListener('click', onOutside);
    return () => document.removeEventListener('click', onOutside);
  }, [open]);

  const formatName = (id: string, prefix: string) => id.replace(prefix, '').split('-').map((s) => s.charAt(0).toUpperCase() + s.slice(1)).join(' ');

  const tree = useMemo((): TreeNode[] => {
    const deptMap = new Map<string, TreeNode>();
    devices.forEach((d) => {
      const dept = deviceDept(d);
      if (!deptMap.has(dept)) {
        const custom = customCollections.find((c) => c.id === dept);
        deptMap.set(dept, {
          id: dept,
          name: custom?.name || (dept === 'Unassigned' ? 'Unassigned' : formatName(dept, 'dept-')),
          type: 'dept',
          folderId: custom?.folderId ?? 'folder-departments-default',
        });
      }
    });
    [{ id: 'dept-engineering', name: 'Engineering' }, { id: 'dept-sales', name: 'Sales' }, { id: 'dept-it', name: 'IT' }, { id: 'dept-hr', name: 'HR' }, { id: 'Unassigned', name: 'Unassigned' }].forEach(
      ({ id, name }) => {
        if (!deptMap.has(id)) deptMap.set(id, { id, name, type: 'dept', folderId: 'folder-departments-default' });
      }
    );
    customCollections.forEach((c) => {
      if (!deptMap.has(c.id)) deptMap.set(c.id, { id: c.id, name: c.name, type: 'dept', folderId: c.folderId });
    });

    const byFolder = new Map<string | null, TreeNode[]>();
    deptMap.forEach((dept) => {
      const fid = dept.folderId ?? null;
      if (!byFolder.has(fid)) byFolder.set(fid, []);
      byFolder.get(fid)!.push(dept);
    });

    const folderNodes: TreeNode[] = customFolders.map((f) => ({
      id: f.id,
      name: f.name,
      type: 'folder',
      children: (byFolder.get(f.id) || []).sort((a, b) => a.name.localeCompare(b.name)),
    }));

    const rootDepts = [...(byFolder.get(null) || []), ...(byFolder.get('folder-departments-default') || [])].filter((d, i, arr) => arr.findIndex((x) => x.id === d.id) === i);
    rootDepts.sort((a, b) => (a.id === 'Unassigned' ? 1 : b.id === 'Unassigned' ? -1 : a.name.localeCompare(b.name)));

    const apiNodes = apiGroups.filter((g) => !g.parent_group_id).map((g) => ({
      id: g.group_id || g.id,
      name: g.name || 'Unnamed',
      type: 'api-group',
    }));

    const items: TreeNode[] = [
      { id: '__all__', name: 'All devices', type: 'all' },
      ...(apiNodes.length ? [{ id: '__dir__', name: 'Directory (OU)', type: 'category', children: apiNodes }] : []),
      { id: '__coll__', name: 'Collections', type: 'category', children: folderNodes.length ? folderNodes : rootDepts },
    ];
    return items;
  }, [devices, customCollections, customFolders, apiGroups]);

  const displayLabel = useMemo(() => {
    if (!value || value === '__all__') return 'All devices';
    const find = (nodes: TreeNode[], id: string): TreeNode | undefined => {
      for (const n of nodes) {
        if (n.id === id) return n;
        if (n.children) { const r = find(n.children, id); if (r) return r; }
      }
      return undefined;
    };
    const node = find(tree, value);
    return node?.name || 'All devices';
  }, [value, tree]);

  const handleSelect = (id: string, name: string) => {
    onChange(id === '__all__' ? null : id, name);
    setOpen(false);
  };

  const handleAddCollection = () => {
    const name = window.prompt('Enter collection name:');
    if (!name?.trim()) return;
    const newId = `dept-custom-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
    const updated = [...customCollections, { id: newId, name: name.trim(), folderId: 'folder-departments-default', createdAt: new Date().toISOString() }];
    setCustomCollections(updated);
    localStorage.setItem('buntu_custom_collections', JSON.stringify(updated));
    handleSelect(newId, name.trim());
  };

  const renderItem = (node: TreeNode, depth: number) => {
    if (node.type === 'category' && node.children) {
      return (
        <div key={node.id} className="py-1">
          <div className="text-xs font-semibold text-slate-500 uppercase px-2 py-1">{node.name}</div>
          {node.children.map((c) => renderItem(c, depth + 1))}
        </div>
      );
    }
    return (
      <button
        key={node.id}
        type="button"
        onClick={() => handleSelect(node.id, node.name)}
        className={`w-full text-left px-3 py-2 text-sm rounded-md flex items-center gap-2 hover:bg-slate-100 ${(value === node.id || (value === null && node.id === '__all__')) ? 'bg-blue-50 text-blue-700 font-medium' : 'text-slate-700'}`}
        style={{ paddingLeft: 8 + depth * 12 }}
      >
        {node.type === 'folder' ? <Folder size={14} className="text-amber-600 flex-shrink-0" /> : node.type === 'dept' ? <Briefcase size={14} className="text-indigo-500 flex-shrink-0" /> : null}
        {node.name}
      </button>
    );
  };

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="flex items-center gap-2 px-4 py-2 border border-slate-300 rounded-lg bg-white text-sm font-medium text-slate-700 hover:bg-slate-50 min-w-[180px] justify-between"
      >
        <span className="truncate">{displayLabel}</span>
        <ChevronDown size={16} className={`text-slate-400 flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="absolute top-full left-0 mt-1 z-50 bg-white border border-slate-200 rounded-lg shadow-lg py-2 max-h-72 overflow-y-auto min-w-[220px]">
          {tree.map((node) => renderItem(node, 0))}
          <div className="border-t border-slate-100 mt-2 pt-2 px-2">
            <button type="button" onClick={handleAddCollection} className="flex items-center gap-2 w-full px-2 py-1.5 text-sm text-blue-600 hover:bg-blue-50 rounded">
              <Plus size={14} /> Add collection
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
